import { useState, useMemo } from "react";
import { Calculator, ChevronDown } from "lucide-react";
import { fmt } from "../lib/money";

/**
 * Sell-price calculator for sellers.
 * Inputs:  desired net (take-home) + commission %
 * Output:  list price = net / (1 - pct/100)
 */
export default function SellPriceCalculator() {
  const [open, setOpen] = useState(false);
  const [net, setNet] = useState("");
  const [pct, setPct] = useState("");

  const result = useMemo(() => {
    const n = parseFloat(net);
    const p = parseFloat(pct);
    if (!isFinite(n) || n <= 0) return null;
    if (!isFinite(p) || p < 0 || p >= 100) return null;
    const list = n / (1 - p / 100);
    const commission = list - n;
    return { list, commission };
  }, [net, pct]);

  return (
    <div
      data-testid="sell-price-calculator"
      className="border border-black bg-[#FFF9E6] mb-6"
    >
      <button
        type="button"
        data-testid="sell-calc-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#FFF3CC] transition-colors"
      >
        <span className="flex items-center gap-2 font-display font-black text-sm sm:text-base">
          <Calculator className="w-5 h-5" />
          Sell-price calculator
          <span className="text-[10px] uppercase tracking-widest text-neutral-600 font-semibold hidden sm:inline">
            — find your list price after commission
          </span>
        </span>
        <ChevronDown
          className={`w-5 h-5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-black px-4 py-4 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 bg-white">
          <div className="space-y-1">
            <label
              htmlFor="calc-net"
              className="text-[11px] uppercase tracking-widest font-bold text-neutral-700"
            >
              You want to keep
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold text-neutral-500">
                G$
              </span>
              <input
                id="calc-net"
                data-testid="sell-calc-net"
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={net}
                onChange={(e) => setNet(e.target.value)}
                placeholder="10000"
                className="w-full border border-black h-11 pl-10 pr-3 font-mono text-base focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
              />
            </div>
          </div>

          <div className="space-y-1">
            <label
              htmlFor="calc-pct"
              className="text-[11px] uppercase tracking-widest font-bold text-neutral-700"
            >
              Commission %
            </label>
            <div className="relative">
              <input
                id="calc-pct"
                data-testid="sell-calc-pct"
                type="number"
                inputMode="decimal"
                min="0"
                max="99"
                step="any"
                value={pct}
                onChange={(e) => setPct(e.target.value)}
                placeholder="10"
                className="w-full border border-black h-11 pl-3 pr-10 font-mono text-base focus:outline-none focus:ring-2 focus:ring-[#0033FF]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-bold text-neutral-500">
                %
              </span>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[11px] uppercase tracking-widest font-bold text-neutral-700">
              List price
            </label>
            <div
              data-testid="sell-calc-result"
              className="h-11 border-2 border-[#0033FF] bg-[#F0F4FF] flex items-center px-3 font-display font-black text-lg sm:text-xl text-[#0033FF]"
            >
              {result ? fmt(result.list) : "—"}
            </div>
          </div>

          {result && (
            <div
              data-testid="sell-calc-breakdown"
              className="sm:col-span-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] pt-1"
            >
              <span>
                Commission paid out:{" "}
                <b className="text-[#FF2A00]">{fmt(result.commission)}</b>
              </span>
              <span>
                You keep:{" "}
                <b className="text-[#00A355]">{fmt(parseFloat(net))}</b>
              </span>
              <span className="text-neutral-500">
                Formula: net ÷ (1 − {pct || 0}%)
              </span>
            </div>
          )}
          {!result && (net || pct) && (
            <div className="sm:col-span-3 text-[12px] text-neutral-500">
              Enter a positive amount and a commission between 0 and 99.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
